import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UtensilsCrossed, Plus, Save, Loader2, Users, Coins, ChefHat, ScanLine } from "lucide-react";
import { toast } from "sonner";
import CantineScanTab from "./CantineScanTab";

const DAYS = ["lundi","mardi","mercredi","jeudi","vendredi","samedi"];
const fmt = (n) => `${Number(n||0).toLocaleString("fr-FR")} GNF`;

export default function CantinePage() {
  const [dashData, setDashData] = useState(null);
  const [weekData, setWeekData] = useState(null);
  const [subs, setSubs] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const monday = getMonday(new Date());
  const [weekStart, setWeekStart] = useState(monday);
  const [form, setForm] = useState({
    week_start: monday, day: "lundi", meal_type: "dejeuner",
    entree: "", plat_principal: "", accompagnement: "", dessert: "",
    is_halal: true, is_vegetarian: false, has_porc: false,
    allergens: [], calories: "", notes: "",
  });

  function getMonday(d) {
    const dt = new Date(d);
    const day = dt.getDay(); const diff = dt.getDate() - day + (day === 0 ? -6 : 1);
    return new Date(dt.setDate(diff)).toISOString().slice(0,10);
  }

  const load = async () => {
    try {
      const [{ data: d }, { data: w }, { data: s }] = await Promise.all([
        api.get("/canteen/dashboard"),
        api.get("/canteen/menu/week", { params: { week_start: weekStart } }),
        api.get("/canteen/subscriptions"),
      ]);
      setDashData(d); setWeekData(w); setSubs(s);
    } catch (e) {
      toast.error("Erreur de chargement");
    }
  };
  useEffect(() => { load(); }, [weekStart]);

  const saveMenu = async () => {
    if (!form.plat_principal) return toast.error("Plat principal obligatoire");
    setSaving(true);
    try {
      await api.post("/canteen/menu", { ...form, calories: form.calories ? Number(form.calories) : null });
      toast.success("Menu enregistré");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const menusByDay = {};
  (weekData?.menus || []).forEach(m => { menusByDay[m.day] = m; });

  return (
    <div className="space-y-6" data-testid="cantine-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="chip mb-2"><UtensilsCrossed className="h-3 w-3" /><span>CANTINE SCOLAIRE</span></div>
          <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Cantine scolaire</h1>
          <p className="text-slate-500 mt-1">Menus hebdomadaires, abonnements et régimes alimentaires.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#EA580C] hover:bg-orange-700 text-white" data-testid="new-menu"><Plus className="h-4 w-4 mr-2" />Ajouter un menu</Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl">
            <DialogHeader><DialogTitle>Nouveau menu</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Semaine (lundi)</Label><Input type="date" value={form.week_start} onChange={e => setForm({...form, week_start: e.target.value})} data-testid="menu-week" /></div>
                <div>
                  <Label>Jour</Label>
                  <Select value={form.day} onValueChange={(v) => setForm({...form, day: v})}>
                    <SelectTrigger data-testid="menu-day"><SelectValue /></SelectTrigger>
                    <SelectContent>{DAYS.map(d => <SelectItem key={d} value={d}>{d.charAt(0).toUpperCase()+d.slice(1)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Entrée</Label><Input value={form.entree} onChange={e => setForm({...form, entree: e.target.value})} placeholder="Salade de crudités" data-testid="menu-entree" /></div>
              <div><Label>Plat principal *</Label><Input value={form.plat_principal} onChange={e => setForm({...form, plat_principal: e.target.value})} placeholder="Riz au poulet yassa" data-testid="menu-plat" /></div>
              <div><Label>Accompagnement</Label><Input value={form.accompagnement} onChange={e => setForm({...form, accompagnement: e.target.value})} placeholder="Légumes vapeur" data-testid="menu-accomp" /></div>
              <div><Label>Dessert</Label><Input value={form.dessert} onChange={e => setForm({...form, dessert: e.target.value})} placeholder="Banane" data-testid="menu-dessert" /></div>
              <div className="flex gap-4 text-sm">
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_halal} onChange={e => setForm({...form, is_halal: e.target.checked})} data-testid="menu-halal" /> Halal</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_vegetarian} onChange={e => setForm({...form, is_vegetarian: e.target.checked})} data-testid="menu-veg" /> Végétarien</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.has_porc} onChange={e => setForm({...form, has_porc: e.target.checked})} /> Contient du porc</label>
              </div>
              <div><Label>Notes / allergènes</Label><Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} data-testid="menu-notes" /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button onClick={saveMenu} disabled={saving} className="bg-[#EA580C] text-white" data-testid="save-menu">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" />Enregistrer</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="apercu" className="space-y-6">
        <TabsList data-testid="cantine-tabs">
          <TabsTrigger value="apercu" data-testid="cantine-tab-apercu"><UtensilsCrossed className="h-4 w-4 mr-1.5" />Aperçu & menus</TabsTrigger>
          <TabsTrigger value="scan" data-testid="cantine-tab-scan"><ScanLine className="h-4 w-4 mr-1.5" />Scan badge</TabsTrigger>
        </TabsList>

        <TabsContent value="apercu" className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KPI label="Abonnements actifs" value={dashData?.active_subs || 0} icon={Users} color="text-[#1E3A8A]" bg="bg-blue-50" />
        <KPI label="Repas servis aujourd'hui" value={dashData?.served_today || 0} icon={ChefHat} color="text-[#166534]" bg="bg-green-50" />
        <KPI label="Revenus du mois" value={fmt(dashData?.revenue_month)} icon={Coins} color="text-[#EA580C]" bg="bg-orange-50" isCurrency />
      </div>

      {/* Weekly menu */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-bold text-xl text-[#1E3A8A]">Menu de la semaine du {weekStart}</h2>
          <Input type="date" value={weekStart} onChange={e => setWeekStart(e.target.value)} className="max-w-xs" data-testid="week-picker" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {DAYS.map(d => (
            <div key={d} className="border border-slate-200 rounded-xl p-4" data-testid={`menu-day-${d}`}>
              <div className="text-xs uppercase font-bold tracking-wider text-slate-500 mb-2">{d}</div>
              {menusByDay[d] ? (
                <div className="space-y-1 text-sm">
                  {menusByDay[d].entree && <div className="text-slate-500">🥗 {menusByDay[d].entree}</div>}
                  <div className="font-bold text-[#1E3A8A]">🍽 {menusByDay[d].plat_principal}</div>
                  {menusByDay[d].accompagnement && <div className="text-slate-600">{menusByDay[d].accompagnement}</div>}
                  {menusByDay[d].dessert && <div className="text-slate-500">🍰 {menusByDay[d].dessert}</div>}
                  <div className="flex gap-1 mt-2 flex-wrap">
                    {menusByDay[d].is_halal && <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-800 rounded font-bold">HALAL</span>}
                    {menusByDay[d].is_vegetarian && <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-800 rounded font-bold">VÉG</span>}
                  </div>
                </div>
              ) : <div className="text-xs text-slate-400 italic">Pas encore de menu</div>}
            </div>
          ))}
        </div>
      </div>

      {/* Subscriptions list */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50">
          <h2 className="font-heading font-bold text-lg text-[#1E3A8A]">Abonnements cantine ({subs.length})</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-2">Élève</th>
                <th className="px-4 py-2">Classe</th>
                <th className="px-4 py-2">Type</th>
                <th className="px-4 py-2">Régime</th>
                <th className="px-4 py-2">Période</th>
                <th className="px-4 py-2 text-right">Prix</th>
                <th className="px-4 py-2">Statut</th>
              </tr>
            </thead>
            <tbody>
              {subs.map(s => (
                <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`sub-${s.id}`}>
                  <td className="px-4 py-2 font-semibold">{s.student_name}</td>
                  <td className="px-4 py-2 text-slate-500">{s.class_name}</td>
                  <td className="px-4 py-2 capitalize">{s.type}</td>
                  <td className="px-4 py-2"><span className="text-xs bg-slate-100 px-2 py-0.5 rounded uppercase font-bold">{s.regime}</span></td>
                  <td className="px-4 py-2 text-xs">{s.start_date} → {s.end_date}</td>
                  <td className="px-4 py-2 text-right tabular-nums font-semibold">{fmt(s.price_gnf)}</td>
                  <td className="px-4 py-2"><span className={`text-xs px-2 py-0.5 rounded font-bold ${s.status === "active" ? "bg-green-100 text-green-800" : "bg-slate-200 text-slate-600"}`}>{s.status}</span></td>
                </tr>
              ))}
              {subs.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Aucun abonnement.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
        </TabsContent>

        <TabsContent value="scan">
          <CantineScanTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function KPI({ label, value, icon: Icon, color, bg }) {
  return (
    <div className={`${bg} rounded-2xl border border-white p-5`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs uppercase tracking-wider font-bold text-slate-500">{label}</span>
        <Icon className={`h-5 w-5 ${color}`} />
      </div>
      <div className={`text-3xl font-extrabold ${color} tabular-nums`}>{value}</div>
    </div>
  );
}
